import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import ScrollReveal from '../components/ScrollReveal';
import TextScramble from '../components/TextScramble';
import { ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SKIN_TYPES = ['All', 'Oily', 'Dry', 'Sensitive'];
const SKIN_TYPE_LABELS = { 'All': 'Tous', 'Oily': 'Grasse', 'Dry': 'S\u00e8che', 'Sensitive': 'Sensible' };

const PRODUCT_TYPES = ['All', 'Soin de visage', 'Soin de corps', 'Compl\u00e9ment alimentaire'];
const PRODUCT_TYPE_LABELS = { 'All': 'Toutes les cat\u00e9gories' };

const SORT_OPTIONS = [
  { value: '', label: 'Trier par' },
  { value: 'popular', label: 'Populaires' },
  { value: 'newest', label: 'Nouveautés' },
  { value: 'price_asc', label: 'Prix croissant' },
  { value: 'price_desc', label: 'Prix décroissant' },
];

export default function ShopPage() {
  const [searchParams] = useSearchParams();
  const typeFromUrl = searchParams.get('type') || 'All';
  const concernFromUrl = searchParams.get('concern') || '';
  const refFromUrl = searchParams.get('ref') || '';

  useEffect(() => {
    if (refFromUrl) {
      try { localStorage.setItem('balmart_referral_code', refFromUrl.toUpperCase()); } catch {}
    }
  }, [refFromUrl]);

  const [products, setProducts] = useState([]);
  const [skinFilter, setSkinFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState(typeFromUrl);
  const [concernFilter, setConcernFilter] = useState(concernFromUrl);
  const [sort, setSort] = useState('');
  const [priceRange, setPriceRange] = useState(''); // '', 'under10k', '10k-25k', '25k-50k', 'over50k'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTypeFilter(typeFromUrl);
    setConcernFilter(concernFromUrl);
  }, [typeFromUrl, concernFromUrl]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (skinFilter !== 'All') params.append('skin_type', skinFilter);
    if (typeFilter !== 'All') params.append('product_type', typeFilter);
    if (concernFilter) params.append('concern', concernFilter);
    if (sort) params.append('sort', sort);
    if (priceRange === 'under10k') params.append('max_price', '10000');
    else if (priceRange === '10k-25k') { params.append('min_price', '10000'); params.append('max_price', '25000'); }
    else if (priceRange === '25k-50k') { params.append('min_price', '25000'); params.append('max_price', '50000'); }
    else if (priceRange === 'over50k') params.append('min_price', '50000');
    const qs = params.toString() ? `?${params.toString()}` : '';
    axios.get(`${API}/products${qs}`)
      .then(res => { setProducts(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [skinFilter, typeFilter, concernFilter, sort, priceRange]);

  const pageTitle = concernFilter ? concernFilter : (typeFilter !== 'All' ? typeFilter : 'Boutique');
  const hasActiveFilters = skinFilter !== 'All' || typeFilter !== 'All' || concernFilter || sort || priceRange;

  return (
    <div data-testid="shop-page" className="pt-24 sm:pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <TextScramble
            text={pageTitle}
            className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-black"
            delay={0.2}
          />
          <p className="mt-4 text-base text-black/50 max-w-lg">
            Des formules scientifiquement prouv&eacute;es pour chaque besoin. Filtrez par cat&eacute;gorie ou type de peau.
          </p>
        </div>

        {/* Category Tabs */}
        <div data-testid="product-type-filters" className="flex flex-wrap gap-3 mb-8">
          {PRODUCT_TYPES.map(type => (
            <button
              key={type}
              data-testid={`type-filter-${type.replace(/\s/g, '-').toLowerCase()}`}
              onClick={() => setTypeFilter(type)}
              className={`text-xs uppercase tracking-[0.15em] font-bold px-5 py-2.5 border transition-all duration-300 ${
                typeFilter === type
                  ? 'bg-black text-white border-black'
                  : 'bg-transparent text-black/50 border-black/15 hover:border-black hover:text-black'
              }`}
            >
              {PRODUCT_TYPE_LABELS[type] || type}
            </button>
          ))}
        </div>

        {/* Skin Type + Sort + Price row */}
        <div className="flex flex-col md:flex-row md:items-end gap-4 mb-12 border-b border-black/10 pb-4">
          {/* Skin Type Filters */}
          <div data-testid="skin-type-filters" className="flex gap-4 sm:gap-6 flex-1">
            {SKIN_TYPES.map(type => (
              <button
                key={type}
                data-testid={`filter-${type.toLowerCase()}`}
                onClick={() => setSkinFilter(type)}
                className={`text-xs uppercase tracking-[0.2em] font-bold pb-2 transition-all duration-300 border-b-2 ${
                  skinFilter === type
                    ? 'text-black border-black'
                    : 'text-black/30 border-transparent hover:text-black/60'
                }`}
              >
                {SKIN_TYPE_LABELS[type] || type}
              </button>
            ))}
          </div>

          {/* Price Range */}
          <div className="relative">
            <select
              data-testid="price-range-select"
              value={priceRange}
              onChange={e => setPriceRange(e.target.value)}
              className="appearance-none bg-transparent border border-black/15 px-4 py-2 pr-9 text-xs uppercase tracking-[0.15em] font-semibold text-black/70 hover:border-black cursor-pointer outline-none"
            >
              <option value="">Prix : Tous</option>
              <option value="under10k">Moins de 10 000 FCFA</option>
              <option value="10k-25k">10 000 - 25 000 FCFA</option>
              <option value="25k-50k">25 000 - 50 000 FCFA</option>
              <option value="over50k">Plus de 50 000 FCFA</option>
            </select>
            <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-black/40" />
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              data-testid="sort-select"
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="appearance-none bg-transparent border border-black/15 px-4 py-2 pr-9 text-xs uppercase tracking-[0.15em] font-semibold text-black/70 hover:border-black cursor-pointer outline-none"
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-black/40" />
          </div>

          {hasActiveFilters && (
            <button
              data-testid="clear-filters-btn"
              onClick={() => { setSkinFilter('All'); setTypeFilter('All'); setConcernFilter(''); setSort(''); setPriceRange(''); }}
              className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/40 hover:text-[#B80000] transition-colors"
            >
              Tout effacer
            </button>
          )}
        </div>

        {/* Results count */}
        {!loading && (
          <p className="text-xs text-black/40 mb-6" data-testid="results-count">
            {products.length} produit{products.length > 1 ? 's' : ''}
          </p>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {[1,2,3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square bg-[#E8E2D8] mb-4" />
                <div className="h-3 bg-[#E8E2D8] w-16 mb-2" />
                <div className="h-5 bg-[#E8E2D8] w-32 mb-2" />
                <div className="h-4 bg-[#E8E2D8] w-12" />
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="products-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {products.map((product, i) => (
              <ScrollReveal key={product.product_id} delay={i * 0.1}>
                <ProductCard product={product} />
              </ScrollReveal>
            ))}
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="text-center py-20">
            <p className="text-black/40 text-sm">Aucun produit trouv&eacute; pour ces filtres.</p>
          </div>
        )}
      </div>
    </div>
  );
}
